import { fileUrl } from "../lib/api";

// Renderizador universal de tickets térmicos (80mm / 58mm) — fuente Arial.
// Recibe un modelo normalizado `doc` (ver lib/ticketDocs.js).
export default function TicketView({ doc, company = {}, format = "80mm", id = "ticket-print" }) {
  if (!doc) return null;
  const narrow = format === "58mm";
  const paperW = narrow ? 58 : 80;
  const fs = narrow ? 12 : 14;
  const fsBig = narrow ? 16 : 18;
  const fsSm = narrow ? 10 : 11;
  const line = { borderTop: "1px dashed #000", margin: "6px 0" };
  const ctr = { textAlign: "center" };
  const rowSB = { display: "flex", justifyContent: "space-between", gap: 6 };
  const cols = narrow ? "3.2ch 1fr 8ch" : "4ch 1fr 8ch 9ch";
  const addr2 = [[company.city, company.state].filter(Boolean).join(", "), company.cp && `C.P. ${company.cp}`].filter(Boolean).join(" ");
  const wrap = { width: `${paperW}mm`, maxWidth: `${paperW}mm`, boxSizing: "border-box", background: "#fff", color: "#000", padding: "4mm", fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: fs, lineHeight: 1.35, margin: "0 auto", wordBreak: "break-word" };
  const num = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n) || 0);

  return (
    <div id={id} style={wrap}>
      <style media="print">{`@page { size: ${paperW}mm auto; margin: 0; }`}</style>
      {company.logo_path && <div style={{ ...ctr, marginBottom: 6 }}><img src={fileUrl(company.logo_path)} alt="logo" style={{ maxWidth: "70%", maxHeight: 140, objectFit: "contain", margin: "0 auto", display: "block" }} /></div>}
      <div style={{ ...ctr, fontWeight: 700, fontSize: fsBig, textTransform: "uppercase", lineHeight: 1.15 }}>{company.name || "MI NEGOCIO"}</div>
      {company.legal_name && <div style={{ ...ctr, fontSize: fsSm }}>{company.legal_name}</div>}
      {company.rfc && <div style={{ ...ctr, fontSize: fsSm }}>RFC: {company.rfc}</div>}
      {company.address && <div style={{ ...ctr, fontSize: fsSm }}>{company.address}</div>}
      {addr2 && <div style={{ ...ctr, fontSize: fsSm }}>{addr2}</div>}
      {company.phone && <div style={{ ...ctr, fontSize: fsSm }}>Tel: {company.phone}</div>}
      {company.email && <div style={{ ...ctr, fontSize: fsSm }}>{company.email}</div>}

      <div style={line} />
      <div style={{ ...ctr, fontWeight: 700, letterSpacing: 1 }}>{doc.title}</div>
      <div style={line} />

      {doc.banner && (
        <div style={{ textAlign: "center", fontWeight: 800, fontSize: 18, letterSpacing: 1, border: "2px solid #000", padding: "4px 2px", margin: "4px 0" }}>{doc.banner}</div>
      )}
      {doc.bannerName && (
        <div style={{ ...ctr, fontWeight: 700, fontSize: fsSm + 2, marginBottom: 2 }}>Cliente: {doc.bannerName}</div>
      )}

      {doc.meta?.map((m, i) => <div key={i} style={rowSB}><span style={{ fontWeight: 700 }}>{m.label}</span><span style={{ textAlign: "right" }}>{m.value}</span></div>)}

      {doc.items?.length > 0 && <>
        <div style={line} />
        <div style={{ display: "grid", gridTemplateColumns: cols, columnGap: 4, fontWeight: 700 }}>
          <span>Cant</span><span>Producto</span>{!narrow && <span style={{ textAlign: "right" }}>P.U.</span>}<span style={{ textAlign: "right" }}>Importe</span>
        </div>
        <div style={{ borderTop: "1px solid #000", margin: "2px 0" }} />
        {doc.items.map((it, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: cols, columnGap: 4, marginBottom: 2 }}>
            <span>{+Number(it.qty).toFixed(3)}</span>
            <span>{it.name}{it.variant ? ` (${it.variant})` : ""}{it.lot ? ` L:${it.lot}` : ""}</span>
            {!narrow && <span style={{ textAlign: "right" }}>{it.price == null ? "" : num(it.price)}</span>}
            <span style={{ textAlign: "right" }}>{it.price == null && it.amount == null ? "" : num(it.amount != null ? it.amount : it.qty * it.price)}</span>
          </div>
        ))}
      </>}

      {doc.rows?.length > 0 && <>
        <div style={line} />
        {doc.rows.map((r, i) => r.sep ? <div key={i} style={line} /> : (
          <div key={i} style={{ ...rowSB, fontWeight: r.bold ? 700 : 400, fontSize: r.big ? fsBig : fs }}><span>{r.label}</span><span style={{ textAlign: "right", whiteSpace: "nowrap" }}>{r.value}</span></div>
        ))}
      </>}

      {doc.blocks?.map((bk, i) => (
        <div key={i}>
          <div style={line} />
          <div style={{ fontWeight: 700 }}>{bk.heading}</div>
          {bk.rows.map((r, j) => <div key={j} style={{ ...rowSB, fontWeight: r.bold ? 700 : 400 }}><span>{r.label}</span><span style={{ textAlign: "right" }}>{r.value}</span></div>)}
        </div>
      ))}

      {doc.totals?.length > 0 && <>
        <div style={line} />
        {doc.totals.map((t, i) => t.big ? (
          <div key={i} style={{ marginTop: 4 }}>
            <div style={{ fontWeight: 700, fontSize: fs }}>{t.label}</div>
            <div style={{ fontWeight: 800, fontSize: fsBig + 4, textAlign: "right", whiteSpace: "nowrap" }}>{t.value}</div>
          </div>
        ) : (
          <div key={i} style={{ ...rowSB, fontWeight: 700, fontSize: fs }}><span>{t.label}</span><span style={{ whiteSpace: "nowrap" }}>{t.value}</span></div>
        ))}
      </>}

      {doc.payment?.length > 0 && <>
        <div style={line} />
        {doc.payment.map((p, i) => <div key={i} style={rowSB}><span>{p.label}</span><span>{p.value}</span></div>)}
      </>}

      {doc.note && <><div style={line} /><div style={{ fontSize: fsSm }}>{doc.note}</div></>}

      {doc.barcode?.svg && <><div style={line} /><div style={{ display: "flex", justifyContent: "center", marginTop: 6 }} data-testid="ticket-barcode" dangerouslySetInnerHTML={{ __html: doc.barcode.svg }} /></>}

      {doc.banners?.map((b, i) => <div key={i} style={{ ...ctr, fontWeight: 700, marginTop: 4 }}>{b}</div>)}

      {doc.signature && <div style={{ marginTop: 26, ...ctr }}>
        <div style={{ borderTop: "1px solid #000", width: "70%", margin: "0 auto" }} />
        <div style={{ fontSize: fsSm, marginTop: 2 }}>{typeof doc.signature === "string" ? doc.signature : "Firma de conformidad"}</div>
      </div>}
      {Array.isArray(doc.signatures) && doc.signatures.map((sg, i) => (
        <div key={i} style={{ marginTop: 28, ...ctr }} data-testid={`ticket-signature-${i}`}>
          <div style={{ borderTop: "1px solid #000", width: "70%", margin: "0 auto" }} />
          <div style={{ fontSize: fsSm, marginTop: 2 }}>{sg}</div>
        </div>
      ))}

      <div style={line} />
      {(doc.footer || doc.isSale) && <div style={{ ...ctr, fontWeight: 700, marginTop: 2 }}>{doc.footer || company.ticket_footer || "GRACIAS POR SU COMPRA"}</div>}
      {doc.isSale && company.warranty_text && <div style={{ ...ctr, fontSize: fsSm, marginTop: 4 }}>{company.warranty_text}</div>}
      <div style={{ ...ctr, fontSize: fsSm - 1, marginTop: 5, color: "#555" }}>--- fin del ticket ---</div>
    </div>
  );
}
